// src/features/blog/components/related-posts.tsx
import { useTranslation } from "react-i18next";
import useEmblaCarousel from "embla-carousel-react";
import { Container } from "../../../shared/ui/container";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";
import { SectionHeader } from "../../../shared/ui/section-header";

interface RelatedPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  image?: string;
  date: string;
  category: { id: string; name: string; slug: string };
  author: string;
}

interface RelatedPostsProps {
  posts: RelatedPost[];
  category: string;
  lang: string;
}

export function RelatedPosts({ posts, category, lang }: RelatedPostsProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [emblaRef] = useEmblaCarousel({ align: "start", direction: isRtl ? "rtl" : "ltr" });

  if (!posts || posts.length === 0) return null;

  return (
    <section className="py-16">
      <Container>
        <SectionHeader title={t("blog.more_reads", { category })} />
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6">
            {posts.map((post) => (
              <a
                key={post.id}
                href={`/${lang}/blog/${post.slug}`}
                className="min-w-0 flex-[0_0_calc(33.333%-16px)] group"
              >
                <div className="overflow-hidden rounded-2xl">
                  <ImageWithFallback
                    src={post.image}
                    alt={post.title}
                    className="aspect-[16/10] w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <h3 className="mt-3 font-sans text-[16px] font-semibold text-text-primary line-clamp-2">
                  {post.title}
                </h3>
                <time className="mt-1 font-sans text-[13px] text-text-muted">{post.date}</time>
              </a>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
